import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, GlobalStyles, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import LeftRail from "./components/LeftRail";
import ChatPanel from "./components/ChatPanel";
import Composer from "./components/Composer";
import DocumentPreview from "./components/DocumentPreview";
import { requestGeneration, buildTemplate, loadDocumentFromSource, applyMetadataField, cleanImportedMarkdown, getMetadataAskMessage, getMetadataFieldLabel } from "./generateDocument";
import { peekHandoff, consumeHandoff, loadFile, saveFile } from "./studioStore";
import { EDIT_SUGGESTIONS, useStudioTheme } from "./constants";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const newConversation = () => ({
  id: newId(),
  title: "",
  messages: [],
  updatedAt: Date.now(),
});

const deriveConvTitle = (text = "") =>
  text.replace(/\s+/g, " ").trim().split(" ").slice(0, 6).join(" ") ||
  "New conversation";

// Merge launcher handoff + any persisted studio state for this file.
const mergeFileState = (fileId, handoff, persisted) => {
  const hasHandoff = Boolean(handoff);
  const hasSource = Boolean(handoff?.sourceUrl || persisted?.sourceUrl);

  const title =
    handoff?.title ||
    persisted?.title ||
    "Untitled document";
  const docType = handoff?.docType || persisted?.docType || "document";
  const sourceUrl = handoff?.sourceUrl || persisted?.sourceUrl || null;

  // When opened from a policy, always reload the real file — don't reuse a
  // stale template that may have been saved from an earlier visit.
  let document = "";
  if (hasHandoff && handoff.sourceUrl) {
    document = handoff.document || "";
  } else if (hasHandoff && handoff.document) {
    document = handoff.document;
  } else if (persisted?.document && !hasHandoff) {
    document = persisted.document;
  } else if (hasSource) {
    document = "";
  } else {
    document = buildTemplate({ title, docType });
  }

  // When launched from a policy link (handoff present), always open with a
  // fresh conversation so the user never sees stale chat from a prior session.
  let conversations;
  if (hasHandoff) {
    conversations = [newConversation()];
  } else {
    conversations = persisted?.conversations;
    if (!conversations || conversations.length === 0) {
      conversations = [newConversation()];
    }
  }
  let activeConversationId = persisted?.activeConversationId;
  if (
    !activeConversationId ||
    !conversations.some((c) => c.id === activeConversationId)
  ) {
    activeConversationId = conversations[0].id;
  }

  return {
    fileId,
    title,
    docType,
    status: handoff?.status || persisted?.status || "template",
    version: handoff?.version ?? persisted?.version ?? null,
    sourceUrl,
    returnTo: handoff?.returnTo || persisted?.returnTo || null,
    document,
    updatedAt: Date.now(),
    conversations,
    activeConversationId,
  };
};

const initFileState = (fileId) => {
  const handoff = peekHandoff(fileId);
  const persisted = loadFile(fileId);
  return mergeFileState(fileId, handoff, persisted);
};

const DocumentGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get("fileId") || "scratch";
  const returnTo = searchParams.get("returnTo");

  const [fileState, setFileState] = useState(() => initFileState(fileId));
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(() => {
    const handoff = peekHandoff(fileId);
    return Boolean(handoff?.sourceUrl);
  });
  const [pendingMetadataFieldId, setPendingMetadataFieldId] = useState(null);

  const pendingMetadataRef = useRef(null);
  useEffect(() => {
    pendingMetadataRef.current = pendingMetadataFieldId;
  }, [pendingMetadataFieldId]);

  const isFirstFileIdRender = useRef(true);

  // Re-init when navigating to a different file in the same tab.
  useEffect(() => {
    if (isFirstFileIdRender.current) {
      isFirstFileIdRender.current = false;
      return;
    }
    setFileState(initFileState(fileId));
    setLoadingDoc(Boolean(peekHandoff(fileId)?.sourceUrl));
  }, [fileId]);

  // Clear the one-shot handoff after mount (peekHandoff keeps StrictMode safe).
  useEffect(() => {
    consumeHandoff(fileId);
  }, [fileId]);

  // When the studio is opened against a source file (e.g. a policy .docx) that
  // hasn't been loaded into markdown yet, fetch + convert it now so the user
  // edits the real content rather than a template.
  useEffect(() => {
    const needsLoad =
      fileState.sourceUrl && (!fileState.document || fileState.document.trim() === "");
    if (!needsLoad) return;

    let cancelled = false;
    setLoadingDoc(true);
    loadDocumentFromSource(fileState.sourceUrl)
      .then((markdown) => {
        if (cancelled) return;
        setFileState((p) => ({
          ...p,
          document:
            markdown && markdown.trim()
              ? markdown
              : buildTemplate({ title: p.title, docType: p.docType }),
          updatedAt: Date.now(),
        }));
      })
      .finally(() => {
        if (!cancelled) setLoadingDoc(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileState.sourceUrl, fileState.fileId]);

  // Ref mirror so async generation reads the latest state.
  const stateRef = useRef(fileState);
  useEffect(() => {
    stateRef.current = fileState;
  }, [fileState]);

  // Persist any change (debounced lightly via microtask is overkill; direct is fine).
  useEffect(() => {
    saveFile(fileState);
  }, [fileState]);

  const activeConversation =
    fileState.conversations.find((c) => c.id === fileState.activeConversationId) ||
    fileState.conversations[0];

  const runGeneration = useCallback(
    async (rawText) => {
      const text = (rawText ?? "").trim();
      if (!text || loading) return;

      setInput("");

      const pendingFieldId = pendingMetadataRef.current;

      // User is answering a follow-up question for a metadata field.
      if (pendingFieldId) {
        setPendingMetadataFieldId(null);

        // Snapshot the document now (ref is up-to-date by the time a user can interact).
        const beforeDoc = cleanImportedMarkdown(stateRef.current.document) || stateRef.current.document;
        const result = applyMetadataField(beforeDoc, pendingFieldId, text);
        const afterDoc = result ? result.document : beforeDoc;
        const reply = result
          ? result.reply
          : "I couldn't locate that placeholder in the document. Try rephrasing?";

        // Add the user message immediately.
        setFileState((prev) => ({
          ...prev,
          conversations: prev.conversations.map((c) =>
            c.id === prev.activeConversationId
              ? { ...c, messages: [...c.messages, { role: "user", content: text }], updatedAt: Date.now() }
              : c
          ),
        }));

        // Metadata changes (company name, author, etc.) are scattered across the doc
        // (title line + body lines), so there is no clean diff prefix to stream from.
        // Snap the document instantly and let the chat reply be the visual feedback.
        setFileState((prev) => ({
          ...prev,
          document: afterDoc,
          updatedAt: Date.now(),
          conversations: prev.conversations.map((c) =>
            c.id === prev.activeConversationId
              ? { ...c, messages: [...c.messages, { role: "assistant", content: reply }], updatedAt: Date.now() }
              : c
          ),
        }));
        return;
      }

      // Append the user message to the active conversation.
      setFileState((prev) => ({
        ...prev,
        updatedAt: Date.now(),
        conversations: prev.conversations.map((c) =>
          c.id === prev.activeConversationId
            ? {
                ...c,
                title: c.messages.length === 0 ? deriveConvTitle(text) : c.title,
                messages: [...c.messages, { role: "user", content: text }],
                updatedAt: Date.now(),
              }
            : c
        ),
      }));

      setLoading(true);
      setStreaming(true);

      const snapshot = stateRef.current;
      const activeConv = snapshot.conversations.find(
        (c) => c.id === snapshot.activeConversationId
      );
      const history = activeConv ? activeConv.messages : [];
      const currentDoc = snapshot.document;

      try {
        const { title, document, reply } = await requestGeneration({
          prompt: text,
          history,
          currentDoc,
          onChunk: (partial) =>
            setFileState((p) => ({ ...p, document: partial })),
        });

        setStreaming(false);
        setFileState((p) => ({
          ...p,
          document,
          title: p.title && p.title !== "Untitled document" ? p.title : title,
          updatedAt: Date.now(),
          conversations: p.conversations.map((c) =>
            c.id === p.activeConversationId
              ? {
                  ...c,
                  messages: [...c.messages, { role: "assistant", content: reply }],
                  updatedAt: Date.now(),
                }
              : c
          ),
        }));
      } catch (e) {
        setStreaming(false);
        setFileState((p) => ({
          ...p,
          conversations: p.conversations.map((c) =>
            c.id === p.activeConversationId
              ? {
                  ...c,
                  messages: [
                    ...c.messages,
                    {
                      role: "assistant",
                      content:
                        "Sorry — I couldn't apply that just now. Please try again.",
                    },
                  ],
                }
              : c
          ),
        }));
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  const handlePickSuggestion = useCallback(
    (suggestion) => {
      if (loading) return;

      const fieldId =
        typeof suggestion === "object" && suggestion.fieldId
          ? suggestion.fieldId
          : null;
      const label =
        typeof suggestion === "string" ? suggestion : suggestion.label;

      if (fieldId) {
        setPendingMetadataFieldId(fieldId);
        setFileState((prev) => ({
          ...prev,
          updatedAt: Date.now(),
          conversations: prev.conversations.map((c) =>
            c.id === prev.activeConversationId
              ? {
                  ...c,
                  title: c.messages.length === 0 ? label : c.title,
                  messages: [
                    ...c.messages,
                    { role: "user", content: label },
                    { role: "assistant", content: getMetadataAskMessage(fieldId) },
                  ],
                  updatedAt: Date.now(),
                }
              : c
          ),
        }));
        return;
      }

      runGeneration(label);
    },
    [loading, runGeneration]
  );

  const handleNewConversation = () => {
    if (loading) return;
    setPendingMetadataFieldId(null);
    const c = newConversation();
    setFileState((p) => ({
      ...p,
      conversations: [c, ...p.conversations],
      activeConversationId: c.id,
    }));
  };

  const handleSelectConversation = (id) => {
    if (loading) return;
    setFileState((p) => ({ ...p, activeConversationId: id }));
  };

  const handleBack = () => {
    if (returnTo) navigate(returnTo);
    else if (window.history.length > 1) navigate(-1);
    else navigate("/admin/dashboard");
  };

  const { palette, fontStack, shadow } = useStudioTheme();

  return (
    <>
      <GlobalStyles styles={{ "body, html, #root": { margin: 0, height: "100%" } }} />
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          width: "100vw",
          overflow: "hidden",
          backgroundColor: palette.canvas,
          fontFamily: fontStack,
        }}
      >
        <LeftRail
          fileTitle={fileState.title}
          docType={fileState.docType}
          conversations={fileState.conversations}
          activeConversationId={fileState.activeConversationId}
          onNewConversation={handleNewConversation}
          onSelectConversation={handleSelectConversation}
          onBack={handleBack}
        />

        <Box sx={{ flex: 1, display: "flex", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
          {/* Conversation column */}
          <Box
            sx={{
              width: { xs: "100%", md: 520 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              backgroundColor: palette.paper,
              borderRight: `1px solid ${palette.border}`,
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: `1px solid ${palette.border}`,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: fontStack,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: palette.ink,
                  letterSpacing: "-0.01em",
                }}
              >
                Edit with Rego AI
              </Typography>
              <Typography
                sx={{
                  fontFamily: fontStack,
                  fontSize: "13px",
                  color: palette.inkSoft,
                  mt: 0.25,
                }}
              >
                Describe changes — the document updates live on the right.
              </Typography>
            </Box>
            <ChatPanel
              messages={activeConversation ? activeConversation.messages : []}
              loading={loading}
              fileTitle={fileState.title}
              suggestions={EDIT_SUGGESTIONS}
              onPickSuggestion={handlePickSuggestion}
            />
            <Box
              sx={{
                px: 2.5,
                pt: 1.5,
                pb: 2.5,
                backgroundColor: palette.paper,
                boxShadow: shadow.panel,
                flexShrink: 0,
              }}
            >
              <Composer
                value={input}
                onChange={setInput}
                onSubmit={() => runGeneration(input)}
                loading={loading}
                variant="docked"
                placeholder={
                  pendingMetadataFieldId
                    ? `Enter ${getMetadataFieldLabel(pendingMetadataFieldId).toLowerCase()}…`
                    : undefined
                }
              />
            </Box>
          </Box>

          {/* Document artifact column (always present) */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: "hidden",
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
            }}
          >
            <DocumentPreview
              title={fileState.title}
              document={fileState.document}
              streaming={streaming}
              loadingDoc={loadingDoc}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DocumentGenerator;
